/**
 * RecruitersTab — assign recruiters to a study and set their round-robin order.
 *
 * Leads for this study auto-assign to the recruiters listed here (in order);
 * with more than one, incoming leads round-robin across them. An empty roster
 * leaves leads unassigned (the shared pool). Editing is admin-gated; recruiters
 * see a read-only view. The roster is keyed by the study's slug server-side, so
 * routing keeps working even before/without further hub setup.
 *
 * StudyDetail keeps all tabs MOUNTED (visibility-toggled), so switching tabs
 * does not re-run load() or drop an in-flight save — local edits persist.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Users, ArrowUp, ArrowDown, X, Plus, Info } from 'lucide-react';
import api from '../../../services/api';
import { StudyDetailResponse } from '../../types';
import { useTeam } from '../../lib';
import { Alert } from '../ui';

interface Row {
  user_id: string;
  name: string | null;
  email: string | null;
  missing?: boolean;
}

export default function RecruitersTab({
  detail,
  isAdmin,
  onChanged,
}: {
  detail: StudyDetailResponse;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const studyId = detail.study.id;
  const { team, byId, loading: teamLoading } = useTeam();

  const [rows, setRows] = useState<Row[]>([]);
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState('');
  const [flash, setFlash] = useState(false); // brief "Saved" confirmation

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/crm/studies/${studyId}/recruiters`);
      const recruiters: Row[] = (res.data?.recruiters || []).map((r: any) => ({
        user_id: r.user_id, name: r.name, email: r.email, missing: r.missing,
      }));
      setRows(recruiters);
      setSavedIds(recruiters.map((r) => r.user_id));
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load recruiters');
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    const cur = rows.map((r) => r.user_id);
    return cur.length !== savedIds.length || cur.some((id, i) => id !== savedIds[i]);
  }, [rows, savedIds]);

  const assignedIds = useMemo(() => new Set(rows.map((r) => r.user_id)), [rows]);
  const available = useMemo(() => team.filter((m) => !assignedIds.has(m.id)), [team, assignedIds]);

  const add = () => {
    if (!adding || assignedIds.has(adding)) return;
    const m = byId.get(adding);
    if (!m) { setError('That recruiter is no longer available — refresh and try again.'); return; }
    setRows((prev) => [...prev, { user_id: adding, name: m.name || null, email: m.email || null }]);
    setAdding('');
  };
  const remove = (id: string) => setRows((prev) => prev.filter((r) => r.user_id !== id));
  const move = (i: number, dir: -1 | 1) =>
    setRows((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/crm/studies/${studyId}/recruiters`, { recruiter_ids: rows.map((r) => r.user_id) });
      setSavedIds(rows.map((r) => r.user_id));
      setFlash(true);
      window.setTimeout(() => setFlash(false), 2000);
      onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save recruiters');
    } finally {
      setSaving(false);
    }
  };

  const iconBtn =
    'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700';

  return (
    <div className="mx-auto max-w-2xl rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Recruiter routing</h2>
      </div>
      <p className="mt-1 flex items-start gap-1.5 text-sm text-gray-500 dark:text-gray-400">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>
          New leads for this study are auto-assigned to the recruiters below.
          {' '}With more than one, leads <strong>round-robin</strong> in this order. Leave empty to keep
          {' '}leads unassigned (shared pool). An existing owner is never changed.
        </span>
      </p>

      {error && (
        <Alert
          tone="error"
          size="sm"
          icon={false}
          className="mt-3"
          actions={
            rows.length === 0 && !loading ? (
              <button
                type="button"
                onClick={load}
                className="flex-shrink-0 rounded-lg border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40"
              >
                Retry
              </button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>
      ) : (
        <>
          {rows.length === 0 ? (
            <p className="mt-4 rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
              No recruiters assigned — leads for this study stay in the shared pool.
            </p>
          ) : (
            <ol className="mt-4 space-y-2">
              {rows.map((r, i) => (
                <li
                  key={r.user_id}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-2.5 dark:border-gray-700 dark:bg-gray-900/40"
                >
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {r.name || r.email || 'Unknown user'}
                      {r.missing && <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">(no longer available)</span>}
                    </p>
                    {r.email && r.name && <p className="truncate text-xs text-gray-400">{r.email}</p>}
                  </div>
                  {isAdmin && (
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className={iconBtn}>
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label="Move down" className={iconBtn}>
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => remove(r.user_id)} aria-label="Remove recruiter" className={iconBtn}>
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}

          {isAdmin ? (
            <div className="mt-4 space-y-3 border-t border-gray-100 pt-4 dark:border-gray-700">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={adding}
                  onChange={(e) => setAdding(e.target.value)}
                  aria-label="Add a recruiter"
                  disabled={teamLoading}
                  className="min-h-[44px] min-w-0 flex-1 basis-48 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:min-h-0 sm:py-2"
                >
                  <option value="">{teamLoading ? 'Loading team…' : available.length ? 'Add a recruiter…' : 'Everyone is assigned'}</option>
                  {available.map((m) => (
                    <option key={m.id} value={m.id}>{m.name || m.email}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={add}
                  disabled={!adding}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-3 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300 sm:min-h-0 sm:py-2"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
              {dirty && (
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={load}
                    disabled={saving}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    {saving ? 'Saving…' : 'Save routing'}
                  </button>
                </div>
              )}
              {!dirty && flash && (
                <p className="text-right text-xs font-medium text-green-600 dark:text-green-400">Saved ✓</p>
              )}
            </div>
          ) : (
            <p className="mt-4 border-t border-gray-100 pt-4 text-xs text-gray-400 dark:border-gray-700">
              Only CRM admins can change recruiter routing.
            </p>
          )}
        </>
      )}
    </div>
  );
}
